import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Subject {
  code: string;
  icon: string;
  name: string;
  desc: string;
  sessions: string;
  badgeClass: string;
}

interface BentoItem {
  icon: string;
  iconClass: string;
  num: string;
  title: string;
  text: string;
  span: string; // grid span class
}

interface FeatureItem {
  icon: string;
  title: string;
  text: string;
}

interface StatItem {
  num: string;
  label: string;
}

interface ContactLink {
  icon: string;
  label: string;
  href: string;
}

interface SocialLink {
  icon: string;
  label: string;
  href: string;
}

@Component({
    selector: 'app-bootcamp',
    templateUrl: './bootcamp.component.html',
    styleUrls: ['./bootcamp.component.css'],
    standalone: false
})
export class BootcampComponent  {

 private observer: IntersectionObserver | null = null;

  stats: StatItem[] = [
    { num: '4', label: 'Subjects' },
    { num: '12 hrs', label: 'Tuition' },
    { num: '720+', label: 'Exam Qs' },
    { num: '2', label: 'Mock Exams' },
    { num: '8 days', label: 'Per Subject' }
  ];

  subjects: Subject[] = [
    {
      code: 'M', icon: 'ti-math-symbols', name: 'Maths',
      desc: 'Number, algebra, data, shape & problem-solving in exam format.',
      sessions: '12 sessions', badgeClass: 'bc-b-navy'
    },
    {
      code: 'E', icon: 'ti-pencil', name: 'English',
      desc: 'Creative writing & comprehension — structure, vocabulary and technique.',
      sessions: '12 sessions', badgeClass: 'bc-b-gold'
    },
    {
      code: 'VR', icon: 'ti-abc', name: 'Verbal Reasoning',
      desc: 'Word patterns, codes, analogies and all GL/CSSE VR question types.',
      sessions: '12 sessions', badgeClass: 'bc-b-cyan'
    },
    {
      code: 'NVR', icon: 'ti-shape', name: 'Non-Verbal Reasoning',
      desc: 'Patterns, sequences, matrices and spatial reasoning for all formats.',
      sessions: '12 sessions', badgeClass: 'bc-b-deep'
    }
  ];

  bentoItems: BentoItem[] = [
    {
      icon: 'ti-clock-hour-4', iconClass: '', num: '12 hrs', title: 'Expert-Led Tuition',
      text: 'Delivered by experienced 11+ tutors who know what the exam boards want.', span: 'bc-bi-1'
    },
    {
      icon: 'ti-file-text', iconClass: 'bc-gold', num: '720+', title: 'Exam-Style Questions',
      text: '12 assignments & 12 practice tests across all four subjects.', span: 'bc-bi-2'
    },
    {
      icon: 'ti-users-group', iconClass: '', num: '2', title: 'In-Person Mocks',
      text: 'CSSE, FSCE or GL format, sat under true exam conditions.', span: 'bc-bi-3'
    },
    {
      icon: 'ti-message-circle', iconClass: 'bc-navy', num: '1 PTM', title: 'Parent-Teacher Meeting',
      text: 'Personalised progress discussion for each student.', span: 'bc-bi-4'
    },
    {
      icon: 'ti-bulb', iconClass: 'bc-gold', num: 'Pre-Lecture', title: 'Materials & Tips',
      text: 'Summary notes and proven tricks to decode tough questions.', span: 'bc-bi-5'
    },
    {
      icon: 'ti-chart-bar', iconClass: '', num: '150–250 Cohort RAR Report', title: '',
      text: 'Standard score, rank & percentile — benchmarked across the full cohort, every centre.', span: 'bc-bi-6'
    }
  ];

  features: FeatureItem[] = [
    {
      icon: 'ti-report-analytics',
      title: 'Cohort Performance Report (RAR)',
      text: 'Consolidated result analysis across all centres — standard score, rank, percentile and more.'
    },
    {
      icon: 'ti-clipboard-check',
      title: 'True Exam Conditions',
      text: 'Timed, invigilated sessions that mirror the real 11+ exam day experience.'
    },
    {
      icon: 'ti-target-arrow',
      title: 'Personalised Feedback',
      text: 'Tailored guidance and clear recommendations on specific areas to improve.'
    },
    {
      icon: 'ti-file-download',
      title: 'Take-Home Review Pack',
      text: 'Papers and full answer keys to take home for continued revision.'
    },
    {
      icon: 'ti-refresh',
      title: 'Ongoing Consultation',
      text: 'Continuous support throughout the course to track progress and close gaps.'
    }
  ];

  contactLinks: ContactLink[] = [
    { icon: 'ti-phone', label: '+44 (0) 3980 8244', href: 'tel:+4403980844' },
    { icon: 'ti-phone', label: '+44 7464 772331', href: 'tel:+447464772331' },
    { icon: 'ti-mail', label: 'support@sehyog.co.uk', href: 'mailto:support@sehyog.co.uk' },
    { icon: 'ti-world', label: 'www.sehyog.co.uk', href: 'https://www.sehyog.co.uk' }
  ];

  socialLinks: SocialLink[] = [
    { icon: 'ti-brand-linkedin', label: 'LinkedIn', href: '#' },
    { icon: 'ti-brand-facebook', label: 'Facebook', href: '#' },
    { icon: 'ti-brand-youtube', label: 'YouTube', href: '#' },
    { icon: 'ti-brand-instagram', label: 'Instagram', href: '#' }
  ];

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.loadFont(
      'bc-font-nunito-space',
      'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Space+Grotesk:wght@500;600;700&display=swap'
    );
    this.loadFont(
      'bc-font-tabler-icons',
      'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css'
    );
  }

  private loadFont(id: string, href: string): void {
    if (document.getElementById(id)) {
      return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  ngAfterViewInit(): void {
    var self = this;
    var options = { root: null, rootMargin: '0px 0px -60px 0px', threshold: 0.12 };

    self.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          if (self.observer) {
            self.observer.unobserve(entry.target);
          }
        }
      });
    }, options);

    var revealEls = self.el.nativeElement.querySelectorAll('.bc-reveal');
    revealEls.forEach(function (node: Element) {
      if (self.observer) {
        self.observer.observe(node);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

 

  
 
  scrollToBook(): void {
    window.location.href = 'https://zfrmz.in/ShF0xJAOzuCmkDyIis9p';
  }
 
  downloadBrochure(): void {
   window.location.href = 'https://drive.google.com/uc?export=download&id=1nDSYjFkdg1ZYRpB15-TBuRi0IBRMLUda';
  }

}
